import { useState, useEffect, useRef } from "react";
import { ChevronDown, Menu, X, Globe, ShoppingCart } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/hooks/useLanguage";
import { useCart } from "@/hooks/useCart";
import logo from "@/assets/logo.webp";

const Header = () => {
  const { lang, setLang, t } = useLanguage();
  const { totalItems } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  const dropdownTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const services = [
    { label: t("service.kondicionieri"), href: "/kondicionieri" },
    { label: t("service.ventilacija"), href: "/ventilacija" },
    { label: t("service.apkure"), href: "/apkure" },
    { label: t("service.projektesana"), href: "/projektesana" },
    { label: t("service.serviss"), href: "/serviss" },
    { label: t("service.konsultacijas"), href: "/konsultacijas" },
  ];

  const navLinks = [
    { label: t("nav.home"), href: "/", isRoute: true },
    { label: t("nav.about"), href: "/par-mums", isRoute: true },
    { label: t("nav.services"), href: "#pakalpojumi", dropdown: services },
    { label: t("nav.projects"), href: "/projekti", isRoute: true },
    { label: t("nav.contacts"), href: "/kontakti", isRoute: true },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleMouseEnter = () => {
    if (dropdownTimeout.current) clearTimeout(dropdownTimeout.current);
    setDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    dropdownTimeout.current = setTimeout(() => setDropdownOpen(false), 150);
  };

  const toggleLang = () => setLang(lang === "lv" ? "en" : "lv");

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-card ${
        scrolled ? "py-2 shadow-md" : "py-4"
      }`}
    >
      <div className="container mx-auto flex items-center justify-between px-4">
        <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
          <img
            src={logo}
            alt="MJ Partneri"
            width={78}
            height={56}
            className={`transition-all duration-300 w-auto ${scrolled ? "h-10 max-w-[56px]" : "h-14 max-w-[78px]"}`}
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-6 xl:gap-8" aria-label={lang === "en" ? "Main navigation" : "Galvenā navigācija"}>
          {navLinks.map((link) =>
            link.dropdown ? (
              <div
                key={link.label}
                className="relative"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <button className="flex items-center gap-1 text-sm font-medium text-foreground hover:text-primary transition-colors min-h-[44px]" aria-expanded={dropdownOpen} aria-haspopup="true">
                  {link.label}
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`} />
                </button>
                <div
                  className={`absolute top-full left-0 pt-2 transition-all duration-200 ${
                    dropdownOpen
                      ? "opacity-100 translate-y-0 pointer-events-auto"
                      : "opacity-0 -translate-y-1 pointer-events-none"
                  }`}
                  role="menu"
                >
                  <div className="w-56 bg-card rounded-lg shadow-lg border border-border py-2">
                    {link.dropdown.map((item) => (
                      <Link
                        key={item.label}
                        to={item.href}
                        className="block px-4 py-2.5 text-sm text-foreground hover:bg-muted hover:text-primary transition-colors"
                        onClick={() => setDropdownOpen(false)}
                        role="menuitem"
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : link.isRoute ? (
              <Link
                key={link.label}
                to={link.href}
                className="text-sm font-medium text-foreground hover:text-primary transition-colors min-h-[44px] flex items-center"
                onClick={() => { if (link.href === "/") window.scrollTo({ top: 0, behavior: "smooth" }); }}
              >
                {link.label}
              </Link>
            ) : (
              <a
                key={link.label}
                href={link.href}
                className="text-sm font-medium text-foreground hover:text-primary transition-colors min-h-[44px] flex items-center"
              >
                {link.label}
              </a>
            )
          )}

          {/* Cart icon */}
          <Link to="/kontakti" className="relative text-foreground hover:text-primary transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center" aria-label={t("kond.cart")}>
            <ShoppingCart className="w-5 h-5" />
            {totalItems > 0 && (
              <span className="absolute top-1 right-0 w-4 h-4 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center">{totalItems}</span>
            )}
          </Link>

          {/* Language switcher - desktop */}
          <button
            onClick={toggleLang}
            className="flex items-center gap-1.5 text-sm font-medium text-foreground hover:text-primary transition-colors px-2.5 py-2 rounded-md border border-border hover:border-primary/30 min-h-[44px]"
            aria-label={lang === "lv" ? "Switch to English" : "Pārslēgt uz latviešu valodu"}
          >
            <Globe className="w-4 h-4" />
            {lang === "lv" ? "EN" : "LV"}
          </button>
        </nav>

        {/* Mobile: cart + lang + toggle */}
        <div className="lg:hidden flex items-center gap-1">
          <Link to="/kontakti" className="relative text-foreground hover:text-primary transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center" aria-label={t("kond.cart")}>
            <ShoppingCart className="w-5 h-5" />
            {totalItems > 0 && (
              <span className="absolute top-2 right-0.5 w-4 h-4 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center">{totalItems}</span>
            )}
          </Link>
          <button
            onClick={toggleLang}
            className="flex items-center gap-1 text-xs font-semibold text-foreground px-2 py-2 rounded border border-border min-w-[44px] min-h-[44px] justify-center"
            aria-label={lang === "lv" ? "Switch to English" : "Pārslēgt uz latviešu valodu"}
          >
            <Globe className="w-4 h-4" />
            {lang === "lv" ? "EN" : "LV"}
          </button>
          <button
            className="text-foreground p-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? (lang === "en" ? "Close menu" : "Aizvērt izvēlni") : (lang === "en" ? "Open menu" : "Atvērt izvēlni")}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile nav */}
      {mobileOpen && (
        <nav className="lg:hidden bg-card border-t border-border px-4 py-4 space-y-1" aria-label={lang === "en" ? "Mobile navigation" : "Mobilā navigācija"}>
          {navLinks.map((link) => (
            <div key={link.label}>
              {link.dropdown ? (
                <>
                  <button
                    className="flex items-center justify-between w-full text-sm font-medium text-foreground hover:text-primary min-h-[44px] px-1"
                    onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)}
                    aria-expanded={mobileDropdownOpen}
                  >
                    {link.label}
                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${mobileDropdownOpen ? "rotate-180" : ""}`} />
                  </button>
                  <div className={`overflow-hidden transition-all duration-200 ${mobileDropdownOpen ? "max-h-96 mt-1" : "max-h-0"}`}>
                    <div className="ml-4 space-y-0">
                      {link.dropdown.map((item) => (
                        <Link
                          key={item.label}
                          to={item.href}
                          className="block text-sm text-muted-foreground hover:text-primary min-h-[44px] flex items-center"
                          onClick={() => setMobileOpen(false)}
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </>
              ) : link.isRoute ? (
                <Link
                  to={link.href}
                  className="block text-sm font-medium text-foreground hover:text-primary min-h-[44px] flex items-center px-1"
                  onClick={() => { setMobileOpen(false); if (link.href === "/") window.scrollTo({ top: 0, behavior: "smooth" }); }}
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  href={link.href}
                  className="block text-sm font-medium text-foreground hover:text-primary min-h-[44px] flex items-center px-1"
                  onClick={() => setMobileOpen(false)}
                >
                  {link.label}
                </a>
              )}
            </div>
          ))}
        </nav>
      )}
    </header>
  );
};

export default Header;
